import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { ArrowLeft, Search, Plus, Barcode, Clock, Loader2, X } from 'lucide-react';
import { searchFood } from '../api/client';
import { useToast } from '../hooks/use-toast';

const tabs = ['All', 'My Foods', 'Meals', 'Recipes'];

// Quick suggestions when no search query - common foods
const quickSuggestions = ['banana', 'apple', 'oatmeal', 'chicken breast', 'egg', 'rice'];

const AddFood = () => {
  const { meal } = useParams();
  const navigate = useNavigate();
  const { addFood } = useApp();
  const { toast } = useToast();
  const [q, setQ] = useState('');
  const [tab, setTab] = useState('All');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState('banana');
  const debounceRef = useRef();

  const search = async (term) => {
    if (!term.trim()) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const data = await searchFood(term, 25);
      setResults(data);
    } catch (e) {
      console.error('food search failed', e);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial: load suggestions
  useEffect(() => {
    search(activeSuggestion);
  }, [activeSuggestion]);

  // Debounced search on input
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q.trim()) return;
    debounceRef.current = setTimeout(() => search(q), 350);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  const handleAdd = async (food) => {
    await addFood(meal, food);
    toast({ title: 'Added to diary', description: `${food.name} added to ${meal}` });
  };

  return (
    <div className="flex flex-col bg-white min-h-screen">
      <header className="bg-[#0066ee] text-white px-4 pt-5 pb-3 sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1.5 rounded-full hover:bg-white/10" aria-label="Back">
            <ArrowLeft size={22} />
          </button>
          <h1 className="text-lg font-bold flex-1">Add Food to {meal}</h1>
          <button className="p-1.5 rounded-full hover:bg-white/10" aria-label="Scan barcode">
            <Barcode size={22} />
          </button>
        </div>
        <div className="mt-3 relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search USDA + Open Food Facts"
            className="w-full bg-white text-gray-900 rounded-xl pl-10 pr-10 py-2.5 text-sm outline-none focus:ring-2 focus:ring-white/40"
            autoFocus
          />
          {q && (
            <button
              onClick={() => setQ('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
              aria-label="Clear"
            >
              <X size={16} />
            </button>
          )}
        </div>
        <div className="mt-3 flex gap-1 overflow-x-auto no-scrollbar">
          {tabs.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                tab === t ? 'bg-white text-[#0066ee]' : 'bg-white/15 text-white hover:bg-white/25'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </header>

      {/* Quick suggestion chips when no query */}
      {!q && (
        <div className="px-4 pt-3 flex gap-2 overflow-x-auto no-scrollbar">
          {quickSuggestions.map((s) => (
            <button
              key={s}
              onClick={() => setActiveSuggestion(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${
                activeSuggestion === s ? 'bg-blue-100 text-[#0066ee]' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="px-4 pt-4 pb-2 flex items-center gap-2 text-gray-500">
        <Clock size={14} />
        <span className="text-xs font-semibold uppercase tracking-wider">
          {q ? `Results (${results.length})` : `Popular: ${activeSuggestion}`}
        </span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 size={26} className="animate-spin text-[#0066ee]" />
        </div>
      ) : (
        <ul className="flex-1 divide-y divide-gray-100">
          {results.map((f) => (
            <li key={f.id} className="px-4 py-3 flex items-center gap-3 hover:bg-gray-50">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{f.name}</p>
                <p className="text-xs text-gray-500 truncate">
                  {Math.round(f.calories)} cal, {f.serving} · {f.brand}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                    f.source === 'usda' ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'
                  }`}>
                    {f.source === 'usda' ? 'USDA' : 'OFF'}
                  </span>
                  <span className="text-[10px] text-gray-500">
                    P {Math.round(f.protein)}g · C {Math.round(f.carbs)}g · F {Math.round(f.fat)}g
                  </span>
                </div>
              </div>
              <button
                onClick={() => handleAdd(f)}
                className="w-9 h-9 rounded-full bg-[#0066ee] text-white flex items-center justify-center shadow-md press-scale hover:bg-[#0055cc]"
                aria-label={`Add ${f.name}`}
              >
                <Plus size={18} />
              </button>
            </li>
          ))}
          {!loading && results.length === 0 && (
            <li className="px-4 py-10 text-center">
              <Search size={32} className="mx-auto text-gray-300" />
              <p className="mt-2 text-sm text-gray-500">No foods found</p>
              <p className="text-xs text-gray-400">Try a different search term</p>
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default AddFood;
